import type { Preferences, PreferencesResponse } from '@/api/preferences';

/**
 * Preferences of the signed-in user (spec §2): stored on the server, with a
 * cache in localStorage keyed by the user so the first paint already uses the
 * user's theme and text size. After sign-in the cache is applied at once, then
 * reconciled with GET /llm/me/preferences. Changes are applied optimistically,
 * saved with PUT, kept as "unsynced" in the cache until the server accepts
 * them, and a failed save is reported so the UI can offer Retry.
 */

export const DEFAULT_PREFERENCES: Preferences = {
  text_size: 'medium',
  theme: 'system',
  language: 'ja',
  width: 'standard',
  follow_latest: true,
  reduce_motion: false,
  send_key: 'ctrl_enter',
};

export type PreferenceKey = keyof Preferences;

const KEYS = Object.keys(DEFAULT_PREFERENCES) as PreferenceKey[];

export const CACHE_PREFIX = 'qa-assist.preferences.v1:';

/** What the cache keeps for one user. */
export interface CacheEntry {
  prefs: Preferences;
  /** Fields changed on this device that the server has not accepted yet. */
  unsynced: PreferenceKey[];
  /** When the oldest unsynced change was made (ms), or null. */
  editedAt: number | null;
}

export interface ServerPreferences {
  data: Preferences;
  stored: boolean;
  updatedAt: string | null;
}

const isPreferences = (value: unknown): value is Preferences =>
  !!value &&
  typeof value === 'object' &&
  KEYS.every(key => key in (value as Record<string, unknown>));

const pick = (prefs: Preferences, keys: Iterable<PreferenceKey>) => {
  const out: Partial<Preferences> = {};
  for (const key of keys) {
    (out as Record<string, unknown>)[key] = prefs[key];
  }
  return out;
};

export const readCache = (
  userKey: string,
  storage: Storage,
): CacheEntry | null => {
  try {
    const raw = storage.getItem(CACHE_PREFIX + userKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CacheEntry>;
    if (!isPreferences(parsed?.prefs)) return null;
    return {
      prefs: { ...DEFAULT_PREFERENCES, ...parsed.prefs },
      unsynced: Array.isArray(parsed.unsynced)
        ? parsed.unsynced.filter((k): k is PreferenceKey => KEYS.includes(k))
        : [],
      editedAt: typeof parsed.editedAt === 'number' ? parsed.editedAt : null,
    };
  } catch {
    return null;
  }
};

export const writeCache = (
  userKey: string,
  entry: CacheEntry,
  storage: Storage,
): void => {
  try {
    storage.setItem(CACHE_PREFIX + userKey, JSON.stringify(entry));
  } catch {
    // A full or blocked storage only costs the flicker protection.
  }
};

/**
 * Merge what the server has with what this device knows.
 *
 * - The server wins, except for
 * - edits made in this session that the server has not acknowledged yet
 *   (`pending`), which always win;
 * - unsynced edits from an earlier session (cache), which win only when they
 *   are newer than the server's last save (another device may have saved
 *   since, and then that save wins);
 * - the language chosen on the sign-in page, which wins while the user has
 *   never saved preferences (`stored: false`).
 *
 * `toPush` is what must be sent to make the server match.
 */
export const reconcile = (
  server: ServerPreferences,
  cache: CacheEntry | null,
  pending: Partial<Preferences>,
  deviceLanguage: Preferences['language'] | null,
): { prefs: Preferences; toPush: Partial<Preferences> } => {
  const prefs: Preferences = { ...server.data };
  const overrides: Partial<Preferences> = {};

  if (!server.stored && deviceLanguage && deviceLanguage !== prefs.language) {
    overrides.language = deviceLanguage;
  }

  if (cache && cache.unsynced.length > 0) {
    const serverTime = server.updatedAt ? Date.parse(server.updatedAt) : NaN;
    const cacheIsNewer =
      Number.isNaN(serverTime) ||
      (cache.editedAt != null && cache.editedAt >= serverTime);
    if (cacheIsNewer) {
      Object.assign(overrides, pick(cache.prefs, cache.unsynced));
    }
  }

  Object.assign(overrides, pending);

  const toPush: Partial<Preferences> = {};
  for (const key of Object.keys(overrides) as PreferenceKey[]) {
    const value = overrides[key];
    if (value !== undefined && value !== prefs[key]) {
      (toPush as Record<string, unknown>)[key] = value;
    }
    (prefs as unknown as Record<string, unknown>)[key] = value;
  }
  return { prefs, toPush };
};

export type LoadSource = 'default' | 'cache' | 'server';

export interface PreferencesState {
  userKey: string | null;
  prefs: Preferences;
  source: LoadSource;
  /** True from start() until the first GET settles. */
  loading: boolean;
  /** True after a failed save, until a save succeeds. */
  saveFailed: boolean;
  saving: boolean;
}

export interface PreferencesDeps {
  get: () => Promise<PreferencesResponse>;
  put: (patch: Partial<Preferences>) => Promise<PreferencesResponse>;
  storage: Storage;
  now?: () => number;
}

const toServer = (response: PreferencesResponse): ServerPreferences | null =>
  response?.success && isPreferences(response.data)
    ? {
        data: { ...DEFAULT_PREFERENCES, ...response.data },
        stored: response.stored !== false,
        updatedAt: response.updated_at ?? null,
      }
    : null;

export class PreferencesStore {
  private state: PreferencesState = {
    userKey: null,
    prefs: DEFAULT_PREFERENCES,
    source: 'default',
    loading: false,
    saveFailed: false,
    saving: false,
  };

  private listeners = new Set<() => void>();
  /** Changes since the last acknowledged save, in this session. */
  private unsynced = new Set<PreferenceKey>();
  /** Changes made while the first GET of start() is in flight. */
  private earlyEdits = new Set<PreferenceKey>();
  private editedAt: number | null = null;
  private inFlight: Promise<void> | null = null;
  private generation = 0;

  constructor(private readonly deps: PreferencesDeps) {}

  private now() {
    return (this.deps.now ?? Date.now)();
  }

  getState = (): PreferencesState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private set(next: Partial<PreferencesState>) {
    this.state = { ...this.state, ...next };
    this.listeners.forEach(listener => listener());
  }

  private persist() {
    const { userKey, prefs } = this.state;
    if (!userKey) return;
    writeCache(
      userKey,
      { prefs, unsynced: [...this.unsynced], editedAt: this.editedAt },
      this.deps.storage,
    );
  }

  /**
   * Sign-in finished: apply this user's cache (or the defaults), then
   * reconcile with the server. `deviceLanguage` is the language the UI is in
   * now (chosen on the sign-in page).
   */
  async start(
    userKey: string,
    deviceLanguage: Preferences['language'] | null,
  ): Promise<void> {
    const generation = ++this.generation;
    const cache = readCache(userKey, this.deps.storage);
    this.unsynced = new Set(cache?.unsynced ?? []);
    this.earlyEdits = new Set();
    this.editedAt = cache?.editedAt ?? null;
    this.set({
      userKey,
      prefs: cache?.prefs ?? {
        ...DEFAULT_PREFERENCES,
        ...(deviceLanguage ? { language: deviceLanguage } : {}),
      },
      source: cache ? 'cache' : 'default',
      loading: true,
      saveFailed: false,
      saving: false,
    });

    let response: PreferencesResponse;
    try {
      response = await this.deps.get();
    } catch {
      response = { success: false };
    }
    if (generation !== this.generation) return;
    const server = toServer(response);
    if (!server) {
      // Keep the cache; unsynced changes go out with the next save.
      this.set({ loading: false });
      return;
    }

    const { prefs, toPush } = reconcile(
      server,
      cache,
      pick(this.state.prefs, this.earlyEdits),
      deviceLanguage,
    );
    this.earlyEdits.clear();
    this.unsynced = new Set(Object.keys(toPush) as PreferenceKey[]);
    this.editedAt =
      this.unsynced.size > 0 ? (this.editedAt ?? this.now()) : null;
    this.set({ prefs, source: 'server', loading: false });
    this.persist();
    if (this.unsynced.size > 0) {
      void this.flush();
    }
  }

  /**
   * Resolves once the first GET of start() has settled (at once when none is
   * in flight), or after timeoutMs, whichever comes first.
   */
  whenLoaded(timeoutMs: number): Promise<void> {
    if (!this.state.loading) return Promise.resolve();
    return new Promise(resolve => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const done = () => {
        if (timer !== undefined) clearTimeout(timer);
        unsubscribe();
        resolve();
      };
      const unsubscribe = this.subscribe(() => {
        if (!this.state.loading) done();
      });
      timer = setTimeout(done, timeoutMs);
    });
  }

  /**
   * Before the first render: apply the cached preferences of the user whose
   * token is stored, without contacting the server (start() follows).
   */
  preview(userKey: string): void {
    const cache = readCache(userKey, this.deps.storage);
    if (cache && !this.state.userKey) {
      this.set({ prefs: cache.prefs, source: 'cache' });
    }
  }

  /** Signed out: forget the user (their cache stays for their next sign-in). */
  stop(): void {
    this.generation++;
    this.unsynced.clear();
    this.earlyEdits.clear();
    this.editedAt = null;
    this.set({
      userKey: null,
      prefs: DEFAULT_PREFERENCES,
      source: 'default',
      loading: false,
      saveFailed: false,
      saving: false,
    });
  }

  /** Change preferences: applied at once, then saved. */
  update(patch: Partial<Preferences>): void {
    const changed = (Object.keys(patch) as PreferenceKey[]).filter(
      key => patch[key] !== undefined && patch[key] !== this.state.prefs[key],
    );
    if (changed.length === 0) return;
    changed.forEach(key => {
      this.unsynced.add(key);
      if (this.state.loading) this.earlyEdits.add(key);
    });
    this.editedAt = this.editedAt ?? this.now();
    this.set({ prefs: { ...this.state.prefs, ...patch } });
    this.persist();
    if (!this.state.loading) {
      void this.flush();
    }
  }

  /** Send every unsynced change again (the Retry of the failure notice). */
  retry(): Promise<void> {
    return this.flush();
  }

  /** Saves one PUT at a time; changes made meanwhile go in the next one. */
  private flush(): Promise<void> {
    if (this.inFlight) {
      return this.inFlight.then(() =>
        this.unsynced.size > 0 ? this.flush() : undefined,
      );
    }
    if (!this.state.userKey || this.unsynced.size === 0) {
      return Promise.resolve();
    }
    const generation = this.generation;
    const sent = pick(this.state.prefs, this.unsynced);
    this.set({ saving: true });
    this.inFlight = this.deps
      .put(sent)
      .then(response => {
        if (generation !== this.generation) return;
        const server = toServer(response);
        if (!server) {
          this.set({ saving: false, saveFailed: true });
          return;
        }
        // Fields changed again while the PUT was in flight stay unsynced.
        for (const key of Object.keys(sent) as PreferenceKey[]) {
          if (this.state.prefs[key] === sent[key]) this.unsynced.delete(key);
        }
        if (this.unsynced.size === 0) this.editedAt = null;
        const prefs = {
          ...server.data,
          ...pick(this.state.prefs, this.unsynced),
        };
        this.set({ prefs, saving: false, saveFailed: false });
        this.persist();
      })
      .catch(() => {
        if (generation === this.generation) {
          this.set({ saving: false, saveFailed: true });
        }
      })
      .finally(() => {
        this.inFlight = null;
      });
    return this.inFlight.then(() =>
      !this.state.saveFailed && this.unsynced.size > 0
        ? this.flush()
        : undefined,
    );
  }
}
